import { AuditLog } from '@/types/auth';
import { appConfig } from '@/config/appConfig';
import { apiClient } from '../api/apiClient';

// Backend audit log format
interface BackendAuditLog {
  action: string;
  entityType: string;
  entityId: string;
  details: string;
}

class AuditLogger {
  private queue: AuditLog[] = [];
  private isProcessing = false;
  private batchSize = 10;
  private flushInterval = 5000; // 5 seconds

  constructor() {
    // Start periodic flush
    if (appConfig.features.auditLogging) {
      setInterval(() => this.flush(), this.flushInterval);
      
      // Flush on page unload
      window.addEventListener('beforeunload', () => this.flush());
    }
  }

  /**
   * Log an audit event
   */
  async log(auditData: Partial<AuditLog>): Promise<void> {
    if (!appConfig.features.auditLogging) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[AuditLog] Disabled via config (REACT_APP_AUDIT_LOGGING=false)');
      }
      return;
    }

    const auditLog: AuditLog = {
      id: this.generateId(),
      userId: auditData.userId || this.getCurrentUserId(),
      action: auditData.action || 'UNKNOWN',
      resource: auditData.resource || 'UNKNOWN',
      timestamp: new Date(),
      ipAddress: await this.getClientIP(),
      userAgent: navigator.userAgent,
      details: auditData.details || {},
      country: auditData.country || this.getCurrentCountry(),
      success: auditData.success !== undefined ? auditData.success : true,
    };

    this.queue.push(auditLog);

    // If queue is full, flush immediately
    if (this.queue.length >= this.batchSize) {
      this.flush();
    }
  }

  /**
   * Flush queued audit logs to server
   */
  private async flush(): Promise<void> {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;
    const logsToSend = [...this.queue];
    this.queue = [];

    try {
      // Send each log individually to match backend API format
      // Use Promise.allSettled so failures don't block other logs
      const promises = logsToSend.map(log => this.sendSingleLog(log));
      const results = await Promise.allSettled(promises);
      
      // Count successes and failures (only in development)
      if (process.env.NODE_ENV === 'development') {
        const successful = results.filter(r => r.status === 'fulfilled').length;
        const failed = results.filter(r => r.status === 'rejected').length;
        
        if (successful > 0) {
          console.debug(`[AuditLog] Sent ${successful}/${logsToSend.length} log(s) successfully`);
        }
        if (failed > 0) {
          console.debug(`[AuditLog] ${failed} log(s) failed (non-critical, continuing)`);
        }
      }
    } catch (error: any) {
      // This should rarely happen since we use Promise.allSettled
      // But if it does, just log in development and continue
      if (process.env.NODE_ENV === 'development') {
        console.debug('[AuditLog] Unexpected error during flush (non-critical):', error.message);
      }
      // Don't re-queue to prevent infinite growth
      // Audit logging is fire-and-forget
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Send a single audit log to backend
   * Non-blocking: errors are caught and logged only in development
   */
  private async sendSingleLog(log: AuditLog): Promise<void> {
    try {
      // Transform to backend format
      const backendLog: BackendAuditLog = {
        action: log.action,
        entityType: log.resource,
        entityId: log.userId,
        details: JSON.stringify({
          timestamp: log.timestamp.toISOString(),
          ipAddress: log.ipAddress,
          userAgent: log.userAgent,
          country: log.country,
          success: log.success,
          ...log.details
        })
      };

      // Use apiClient with shorter timeout for audit logs (5 seconds)
      // Note: Audit endpoint is at /api/v2/AuditLog (not v1)
      const baseUrl = appConfig.api.apiUrl.replace('/api/v1', '/api/v2');
      await apiClient.request({
        url: `${baseUrl}/AuditLog`,
        method: 'POST',
        data: backendLog,
        skipErrorHandler: true,
        timeout: 10000, // 5 second timeout for audit logs
      });
    } catch (error: any) {
      // Silently fail - audit logging should never break the app
      if (process.env.NODE_ENV === 'development') {
        // Only log audit failures in development
        const errorMsg = error?.message || error?.response?.statusText || 'Unknown error';
        const statusCode = error?.response?.status;
        console.debug(`[AuditLog] Failed to send (non-critical): ${errorMsg}${statusCode ? ` (${statusCode})` : ''}`);
      }
      // Don't throw - audit logging is fire-and-forget
    }
  }

  /**
   * Get current user ID from stored user data
   */
  private getCurrentUserId(): string {
    try {
      const userData = localStorage.getItem('current_user');
      if (userData) {
        const user = JSON.parse(userData);
        return user.id || 'anonymous';
      }
    } catch {
      // Ignore parsing errors
    }
    return 'anonymous';
  }

  /**
   * Get current country from user preferences or default
   */
  private getCurrentCountry(): string {
    try {
      const userData = localStorage.getItem('current_user');
      if (userData) {
        const user = JSON.parse(userData);
        return user.countries?.[0] || 'EG';//config.countries.default;
      }
    } catch {
      // Ignore parsing errors
    }
    return 'EG';//config.countries.default;
  }

  /**
   * Get client IP address (simplified - in real app, this would be more sophisticated)
   */
  private async getClientIP(): Promise<string> {
    try {
      // In a real application, you might get this from a service
      // or include it in the initial page load
      return 'unknown';
    } catch {
      return 'unknown';
    }
  }

  /**
   * Generate unique ID for audit log
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Log user action with automatic resource detection
   */
  async logUserAction(action: string, details?: Record<string, any>): Promise<void> {
    const resource = this.extractResourceFromAction(action);
    await this.log({
      action,
      resource,
      details,
    });
  }

  /**
   * Extract resource name from action
   */
  private extractResourceFromAction(action: string): string {
    const actionMap: Record<string, string> = {
      'LOGIN': 'AUTH',
      'LOGOUT': 'AUTH',
      'LOGIN_FAILED': 'AUTH',
      'VIEW_REPORT': 'REPORT',
      'EXPORT_REPORT': 'REPORT',
      'CREATE_REPORT': 'REPORT',
      'UPDATE_REPORT': 'REPORT',
      'DELETE_REPORT': 'REPORT',
      'VIEW_DASHBOARD': 'DASHBOARD',
      'UPDATE_DASHBOARD': 'DASHBOARD',
      'VIEW_USER': 'USER',
      'CREATE_USER': 'USER',
      'UPDATE_USER': 'USER',
      'DELETE_USER': 'USER',
      'VIEW_ROLE': 'ROLE',
      'CREATE_ROLE': 'ROLE',
      'UPDATE_ROLE': 'ROLE',
      'DELETE_ROLE': 'ROLE',
    };

    return actionMap[action] || 'UNKNOWN';
  }
}

export const auditLogger = new AuditLogger();
export default auditLogger;

